/**
 * Appointment-type duration.
 *
 * The settings form edits hours and minutes. Everything else keeps reading
 * one total in `duration_minutes`. The database allows 1 through 2880
 * minutes (48 hours). Appointments store `starts_at`/`ends_at`, not a
 * separate duration column.
 */

export const SERVICE_DURATION_PRESETS: readonly number[] = [15, 20, 30, 45, 60, 75, 90, 120];

export const SERVICE_DURATION_MIN = 1;
/** 48 hours, stored as minutes. */
export const SERVICE_DURATION_MAX = 48 * 60;
export const SERVICE_DURATION_MAX_HOURS = 48;
export const SERVICE_DURATION_MINUTE_PART_MAX = 59;

export const SERVICE_DURATION_HOURS_ID = 'service-duration-hours';
export const SERVICE_DURATION_MINUTES_ID = 'service-duration-minutes';

export const INVALID_SERVICE_DURATION = 'invalid_duration_minutes';

export function isPresetServiceDuration(minutes: number): boolean {
  return SERVICE_DURATION_PRESETS.includes(minutes);
}

export function isValidServiceDurationMinutes(minutes: number): boolean {
  return (
    Number.isInteger(minutes) && minutes >= SERVICE_DURATION_MIN && minutes <= SERVICE_DURATION_MAX
  );
}

/** Parse a typed minute count. Empty, decimal, and out-of-range values are rejected. */
export function parseServiceDurationMinutes(raw: string): number | null {
  const parsed = parseNonNegativeInteger(raw);
  if (parsed == null) return null;
  return isValidServiceDurationMinutes(parsed) ? parsed : null;
}

export function parseNonNegativeInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isInteger(value) ? value : null;
}

export function splitServiceDuration(totalMinutes: number): { hours: number; minutes: number } {
  if (!Number.isInteger(totalMinutes) || totalMinutes <= 0) {
    return { hours: 0, minutes: 0 };
  }
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

/** Combine the hours and minutes fields. Empty or out-of-range parts are rejected. */
export function combineServiceDuration(hoursRaw: string, minutesRaw: string): number | null {
  const hours = parseNonNegativeInteger(hoursRaw);
  const minutes = parseNonNegativeInteger(minutesRaw);
  if (hours == null || minutes == null) return null;
  if (hours > SERVICE_DURATION_MAX_HOURS || minutes > SERVICE_DURATION_MINUTE_PART_MAX) return null;
  return parseServiceDurationMinutes(String(hours * 60 + minutes));
}

/**
 * Minutes to persist. When the hours and minutes fields are on the form,
 * their current text wins. Otherwise the already stored total is used.
 */
export function durationMinutesForSave(form: ParentNode, selectedMinutes: number): number | null {
  const hours = form.querySelector<HTMLInputElement>(`#${SERVICE_DURATION_HOURS_ID}`);
  const minutes = form.querySelector<HTMLInputElement>(`#${SERVICE_DURATION_MINUTES_ID}`);
  if (!hours && !minutes) {
    return isValidServiceDurationMinutes(selectedMinutes) ? selectedMinutes : null;
  }
  if (!hours || !minutes) return null;
  return combineServiceDuration(hours.value, minutes.value);
}

export function assertServiceDurationMinutes(minutes: number): number {
  if (!isValidServiceDurationMinutes(minutes)) {
    throw new Error(INVALID_SERVICE_DURATION);
  }
  return minutes;
}

export function isInvalidServiceDurationError(err: unknown): boolean {
  return err instanceof Error && err.message === INVALID_SERVICE_DURATION;
}
